import { useEffect, useSyncExternalStore } from 'react'

/**
 * The one rule for how MyLib rearranges between a large and a small screen.
 *
 * A lane joins the row as soon as there is room for it; until then the lanes are visited one after
 * the other, and the frame's back control returns to the previous one. That gives three steps:
 *
 *   - `compact`  — one lane. The areas sit along the bottom, in reach of a thumb.
 *   - `medium`   — the areas move into a rail beside the content, which is still one lane.
 *   - `wide`     — an overview and what is open inside it stand beside each other as well.
 *
 * The decision is made here and nowhere else. The steps are named on the root element as
 * `data-layout`, so the stylesheets react to a name rather than repeating the widths, and every
 * area gets the same behaviour without deciding anything of its own.
 */
export const LAYOUT_STEPS = ['compact', 'medium', 'wide'] as const

export type LayoutStep = (typeof LAYOUT_STEPS)[number]

/**
 * Where the steps change over. 768px is where a rail beside the content stops taking the content's
 * room, 1216px is where a second lane still leaves the first one readable.
 *
 * Media queries are matched here rather than written into the stylesheets, so these two numbers
 * exist once. `px` and not `rem` on purpose: a media query measures `rem` against the browser's
 * initial font size, not against the root size MyLib sets, which would make the unit misleading.
 */
export const LAYOUT_QUERIES: Readonly<Record<Exclude<LayoutStep, 'compact'>, string>> = {
  medium: '(min-width: 768px)',
  wide: '(min-width: 1216px)',
}

function matches(query: string): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(query).matches
}

export function currentLayoutStep(): LayoutStep {
  if (matches(LAYOUT_QUERIES.wide)) {
    return 'wide'
  }
  return matches(LAYOUT_QUERIES.medium) ? 'medium' : 'compact'
}

function subscribe(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') {
    return () => {}
  }
  const lists = Object.values(LAYOUT_QUERIES).map((query) => window.matchMedia(query))
  for (const list of lists) {
    list.addEventListener('change', onChange)
  }
  return () => {
    for (const list of lists) {
      list.removeEventListener('change', onChange)
    }
  }
}

/** The step the window is at right now, and again whenever it changes. */
export function useLayoutStep(): LayoutStep {
  return useSyncExternalStore(subscribe, currentLayoutStep, () => 'wide')
}

/**
 * Publishes the step as `data-layout` on the root element and keeps it there.
 *
 * On the root rather than on the frame, because the standalone screens are laid out by the same
 * rule and there is no frame around them.
 */
export function useLayoutAttribute(): LayoutStep {
  const step = useLayoutStep()

  useEffect(() => {
    document.documentElement.dataset.layout = step
  }, [step])

  return step
}

/** Whether an overview and the thing open inside it can stand beside each other. */
export function lanesFitBesideEachOther(step: LayoutStep): boolean {
  return step === 'wide'
}

/**
 * Whether a board can be **rearranged** here, as opposed to merely looked at and walked over.
 *
 * `false` on the smallest step, and that is a decision rather than a limit somebody ran into.
 * Arranging things spatially needs room to see what is beside what and a pointer precise enough to
 * put something down where it is meant to go; on a screen the size of a hand it is a worse version
 * of what an iPad does well. So a phone gets the whole board — panning, zooming, opening a card,
 * finding something on it — and none of the dragging.
 *
 * Named here, beside the one layout rule, so that nothing in `views/desk/` ever mentions a device.
 */
export function boardCanBeRearranged(step: LayoutStep): boolean {
  return step !== 'compact'
}
